import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env';
import { kayshFlushOperator, kayshOperator } from '@karacas/kaysh';
import { _log, _logTap, _useDummyData } from '@shared/aux_helper_environment';
import { _get, _handleErrorWithDummyData, _ms, _throwError } from '@shared/aux_helper_functions';
import { Observable, of } from 'rxjs';
import { catchError, delay, map, switchMap, tap } from 'rxjs/operators';
import {
  PromotionalProcessFormModel,
  PromotionalProcessItem,
  PromotionalProcessList,
  PromotionalProcessTasksFromBe,
  TaskStartingDayPosition,
} from '../models/promotionalProcess.model';
import { PROMOTIONAL_PROCESS_DUMMY_DATA, PROMOTIONAL_PROCESS_ITEM_DUMMY_DATA } from '../models/promotionalProcessDummy';
import { PagedList } from 'core/models/paged-list.model';

@Injectable({
  providedIn: 'root',
})
export class PromotionalProcessService {
  private configBase = _get(environment, 'apiBaseUrl_promotionV2');
  private configProcess = _get(environment, 'promotionV2.process.api');
  USE_DUMMY_DATA = false;

  constructor(private http: HttpClient) {}

  /**
   * corregir mapeo cuando este listo el endpoint de BE
   */
  getPromotionalProcessList(filters): Observable<PagedList<PromotionalProcessList>> {
    const USE_DUMMY_DATA = false;
    if (USE_DUMMY_DATA) {
      return of(PROMOTIONAL_PROCESS_DUMMY_DATA).pipe(delay(2000));
    }
    const qs: Array<string> = ['?'];

    if (filters.pageIndex) {
      qs.push(`pageIndex=${filters.pageIndex}`);
    }

    if (filters.pageSize) {
      qs.push(`pageSize=${filters.pageSize}`);
    }

    const url = `${this.configBase}${this.configProcess.resources.getPromotionProcessList}${qs.join('&')}`;

    return this.http.post<PagedList<PromotionalProcessList>>(url, filters).pipe(
      tap(_data => _logTap(`${PromotionalProcessService.name}::getPromotionalProcessList (tap)\n\tdata: %o`, _data)),
      kayshOperator('getPromotionalProcessList', filters, {
        localStorage: false,
        maxItems: 100,
        maxTime: _ms('60m'),
      }),
      catchError(error => {
        if (!_useDummyData()) {
          return _throwError('NO DATA', 'getPromotionalProcessList');
        }
        let rv = PROMOTIONAL_PROCESS_DUMMY_DATA;
        return of(rv).pipe(delay(500));
      })
    );
  }

  clearCachePromotionalProcessList() {
    return kayshFlushOperator('getPromotionalProcessList');
  }

  getPromotinoalProcessById(id): Observable<any> {
    if (this.USE_DUMMY_DATA) return of(PROMOTIONAL_PROCESS_ITEM_DUMMY_DATA).pipe(delay(2000), map(_mapItemFromBe));

    const url = `${this.configBase}${this.configProcess.resources.getPromotionProcess}/${id}`;

    return this.http.get(url).pipe(
      tap(data => _logTap(`${PromotionalProcessService.name}::getPromotinoalProcessById (tap)\n\tdata: %o`, data)),
      map(data => _mapItemFromBe(data)),
      kayshOperator('getPromotinoalProcessById', id, { maxTime: _ms('1h'), useCacheReplay: false }),
      catchError(error =>
        _handleErrorWithDummyData(
          true && _useDummyData(),
          error,
          PromotionalProcessService,
          _mapItemFromBe(PROMOTIONAL_PROCESS_ITEM_DUMMY_DATA),
          'getPromotinoalProcessById'
        )
      )
    );
  }

  clearCachePromotionalProcessById() {
    return kayshFlushOperator('getPromotinoalProcessById');
  }

  promotionTypeListClearCache() {
    return kayshFlushOperator('getPromotionProcessList');
  }

  savePromotionalProcess(payload: PromotionalProcessFormModel): Observable<PromotionalProcessFormModel> {
    if (this.USE_DUMMY_DATA) return of(PROMOTIONAL_PROCESS_ITEM_DUMMY_DATA).pipe(delay(2000), map(_mapItemFromBe));
    const _url = `${this.configBase}${this.configProcess.resources.savePromotionalProcess}`;
    return this.http.post<PromotionalProcessItem>(_url, _mapPromotionalProcessToBe(payload)).pipe(
      tap(data => _logTap(`${PromotionalProcessService.name}::savePromotionalProcess (tap)\n\tdata: %o`, data)),
      map(data => _mapItemFromBe(data))
    );
  }

  _cache_checkCodeAlreadyExist = {};
  checkCodeAlreadyExist($code: string, type?: any): Observable<boolean> {
    let code = String($code).trim();

    if (!code?.length) return of(false);

    let cache = this._cache_checkCodeAlreadyExist[code + (type ?? '')];

    if (cache != null) {
      return of(cache);
    }

    let url = `${this.configBase}${this.configProcess.resources.getCodeExist}/${code}`;

    return this.http.get<boolean>(url).pipe(
      delay(128 * 3),
      tap(data => {
        _logTap(`${PromotionalProcessService.name}::checkPromotionCodeNotTaken (tap)\n\tdata: %o`, data);
      }),
      tap(data => (this._cache_checkCodeAlreadyExist[code + (type ?? '')] = data)),
      catchError(error => {
        if (_useDummyData()) {
          const result = false;
          this._cache_checkCodeAlreadyExist[code + (type ?? '')] = result;
          return of(result).pipe(delay(128 * 3));
        }

        return of(false);
      })
    );
  }
  reset_cache_checkInternalCodeAlreadyExist() {
    this._cache_checkCodeAlreadyExist = {};
  }

  deletePromotionalProcess(id: number): Observable<any> {
    const url = `${this.configBase}${this.configProcess.resources.deletePromotionalProcess}/${id}`;

    return this.http
      .post(url, id)
      .pipe(tap(data => _logTap(`${PromotionalProcessService.name}::deletePromotionalProcess (tap)\n\tdata: %o`, data)));
  }
}

const _mapItemFromBe = (data): PromotionalProcessFormModel => {
  if (!data) {
    return {
      mainData: {
        id: 0,
        name: '',
        code: '',
        inUse: false,
        minimumCoverage: 85,
      },
      tasksTab: {
        tasks: [],
      },
    };
  }

  return {
    mainData: {
      id: data.id || 0,
      name: data.name || '',
      code: data.code || '',
      inUse: data.inUse || false,
      minimumCoverage: Number((data.minimumCoverage * 100).toFixed(2)) || 85,
    },
    tasksTab: {
      tasks: (data.tasks || []).map((task: PromotionalProcessTasksFromBe) => ({
        ...task,
        requiresCompletion: !!task?.requiresCompletion,
        internalId: `task-${task.id}`,
        startingDay: task.startingDay < 0 ? -task.startingDay : task.startingDay,
        startingDayPosition: task?.startingDay < 0 ? TaskStartingDayPosition.before : TaskStartingDayPosition.after,
      })),
    },
  };
};

const _mapPromotionalProcessToBe = (data: PromotionalProcessFormModel): PromotionalProcessItem => {
  return {
    ...data?.mainData,
    minimumCoverage: Number(data.mainData?.minimumCoverage / 100),
    tasks:
      data?.tasksTab?.tasks?.map(({ internalId, startingDayPosition, startingDay, ...task }) => ({
        startingDay: startingDayPosition === TaskStartingDayPosition.before ? -Number(startingDay) : Number(startingDay),
        ...task,
      })) || [],
  };
};
